// Indee returns screener descriptions as HTML fragments ("<p>Some copy.</p>"),
// but we render them as text, so the markup shows up on the page. This turns a
// fragment into the plain text it represents.
//
// Deliberately a pure function rather than innerHTML/DOMParser: nothing is
// handed to the browser's HTML parser, so there is no way for markup in a
// description to load a resource or fire an event handler.

const NAMED_ENTITIES = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  ensp: " ",
  emsp: " ",
  thinsp: " ",
  shy: "",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
  bull: "•",
  middot: "·",
  trade: "™",
  reg: "®",
  copy: "©",
  deg: "°",
};

// A quoted attribute value may itself contain ">", so skip over quoted runs
const ATTRS = '[^>"\']*(?:(?:"[^"]*"|\'[^\']*\')[^>"\']*)*';

// Tags that imply a line break, so "<p>One</p><p>Two</p>" doesn't read "OneTwo"
const BLOCK_BOUNDARY = new RegExp(
  "</?(?:p|div|br|hr|li|ul|ol|tr|td|th|h[1-6]|section|article|header|footer" +
    "|blockquote|pre|figure|figcaption|dl|dt|dd|table|tbody|thead|address)\\b" +
    ATTRS +
    ">",
  "gi",
);

const ANY_TAG = new RegExp("</?[a-zA-Z!][a-zA-Z0-9:-]*" + ATTRS + ">", "g");

function decodeEntities(text) {
  return text.replace(/&(#x?[0-9a-f]+|[a-z][a-z0-9]*);/gi, (match, body) => {
    if (body[0] === "#") {
      const hex = body[1] === "x" || body[1] === "X";
      const code = parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return match;
      try {
        return String.fromCodePoint(code);
      } catch (error) {
        return match;
      }
    }
    const named = NAMED_ENTITIES[body.toLowerCase()];
    return named === undefined ? match : named;
  });
}

export function htmlToText(input) {
  if (input === null || input === undefined) return "";

  let text = String(input);

  // Drop comments, and script/style along with their contents
  text = text.replace(/<!--[\s\S]*?-->/g, " ");
  text = text.replace(/<!\[CDATA\[[\s\S]*?\]\]>/g, " ");
  text = text.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, " ");
  // An unclosed script/style would otherwise leak its contents as text
  text = text.replace(/<(script|style)\b[^>]*>[\s\S]*$/gi, " ");

  text = text.replace(BLOCK_BOUNDARY, " ");
  text = text.replace(ANY_TAG, "");
  // An unclosed tag at the end (e.g. copy truncated upstream) is dropped. It
  // has to look like a tag name: a bare "<" in prose ("scores < 50") is text.
  text = text.replace(/<\/?[a-zA-Z!][^<]*$/, "");

  // Decode after stripping so text that was escaped on purpose ("&lt;p&gt;")
  // survives as visible characters instead of being treated as a tag
  text = decodeEntities(text);

  return text.replace(/\s+/g, " ").trim();
}

export default htmlToText;
